import { getHexTileMapKey } from '../hexmap/model/keys';
import { axialNeighbors } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import {
  type Building,
  buildingIndex,
  buildingLevel,
  type Cell,
  type EntityId,
  type PlayerId,
  type SimContext,
  type SimState,
  type TileVisibility,
  territoryOwners,
} from '../sim';

/**
 * City sprawl: purely cosmetic. From L5 a completed city spreads over free
 * land next to it (`SPRAWL_TILES` by level: ring 1 only, so at most 4 tiles
 * with the city). Candidates are any land tile (not water) in that city's own
 * territory with no building or footprint on it, ordered by a stable hash of
 * city id + tile key, so the layout only shifts when a building takes a tile.
 * Gameplay ignores it: the tile keeps its resources and stays placeable.
 */

/** Sprawl tiles by city level (index = level - 1); levels past the end use the last. */
const SPRAWL_TILES = [0, 0, 0, 0, 1, 2, 3] as const;

interface SprawlTile {
  key: HexTileMapKey;
  pos: Cell;
  cityId: EntityId;
  owner: PlayerId;
  /** The city's level, which picks the era look. */
  level: number;
  /** Which of the era's looks to draw (stable per tile; the model wraps it). */
  variant: number;
}

/** Sprawl tiles a city of `level` gets. */
function sprawlCount(level: number): number {
  return SPRAWL_TILES[Math.min(Math.max(level, 1), SPRAWL_TILES.length) - 1] ?? 0;
}

/** FNV-1a 32-bit hash: stable on every peer, no RNG state. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function computeSprawl(state: SimState, ctx: SimContext): Map<HexTileMapKey, SprawlTile> {
  const result = new Map<HexTileMapKey, SprawlTile>();
  const cities: Building[] = Object.values(state.buildings).filter(
    (b) =>
      b.buildProgress >= 1 &&
      ctx.rules.buildings[b.type]?.territoryRadius !== undefined &&
      sprawlCount(buildingLevel(b)) > 0
  );
  if (cities.length === 0) return result;
  // Computed once for every city.
  const owners = territoryOwners(state, ctx.rules);
  const covered = buildingIndex(state);
  for (const city of cities) {
    const level = buildingLevel(city);
    const candidates = axialNeighbors(city.pos)
      .map((pos) => ({ pos, key: getHexTileMapKey(pos) }))
      .filter(({ key }) => {
        const tile = ctx.terrain.get(key);
        return (
          tile !== undefined &&
          tile.type !== 'water' &&
          owners.get(key) === city.id &&
          !covered.has(key)
        );
      })
      .map((c) => ({ ...c, order: hash(`${city.id}:${c.key}`) }))
      .sort((a, b) => a.order - b.order || (a.key < b.key ? -1 : 1));
    for (const { pos, key } of candidates.slice(0, sprawlCount(level))) {
      result.set(key, {
        key,
        pos,
        cityId: city.id,
        owner: city.owner,
        level,
        variant: hash(`sprawl:${key}`),
      });
    }
  }
  return result;
}

// Last result, reused while the state and its tick are unchanged (the sim
// mutates state in place once per tick; snapshots arrive as new objects).
let cache: {
  state: SimState;
  tick: number;
  ctx: SimContext;
  result: ReadonlyMap<HexTileMapKey, SprawlTile>;
} | null = null;

/** Every city's sprawl tiles (tile key → tile), memoised per tick. */
function citySprawl(state: SimState, ctx: SimContext): ReadonlyMap<HexTileMapKey, SprawlTile> {
  if (
    cache &&
    cache.state === state &&
    cache.tick === state.tick &&
    cache.ctx.terrain === ctx.terrain &&
    cache.ctx.rules === ctx.rules
  )
    return cache.result;
  const result = computeSprawl(state, ctx);
  cache = { state, tick: state.tick, ctx, result };
  return result;
}

/**
 * The sprawl a viewer is shown: their own always; a rival's only while both
 * the sprawl tile and its city's tile are in sight, so a tile seen at the edge
 * of vision never reveals the hidden city next to it or its era, and sprawl
 * never vanishes ahead of the city's "as last seen" marker when it falls.
 */
function visibleSprawl(
  sprawl: ReadonlyMap<HexTileMapKey, SprawlTile>,
  state: SimState,
  playerId: PlayerId,
  visibility: (key: HexTileMapKey) => TileVisibility
): Map<HexTileMapKey, SprawlTile> {
  const result = new Map<HexTileMapKey, SprawlTile>();
  for (const [key, tile] of sprawl) {
    if (tile.owner !== playerId) {
      const city = state.buildings[tile.cityId];
      if (!city || visibility(key) !== 'visible') continue;
      if (visibility(getHexTileMapKey(city.pos)) !== 'visible') continue;
    }
    result.set(key, tile);
  }
  return result;
}

/**
 * The rival city a viewer means by a click or hover on `key`: the city whose
 * sprawl covers it, only while that sprawl is shown to them (`visibleSprawl`),
 * so a sprawl tile reads as part of its city without leaking the fog.
 */
function rivalSprawlCity(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  visibility: (key: HexTileMapKey) => TileVisibility,
  key: HexTileMapKey
): Building | null {
  const tile = citySprawl(state, ctx).get(key);
  if (!tile || tile.owner === playerId) return null;
  if (visibleSprawl(new Map([[key, tile]]), state, playerId, visibility).size === 0) return null;
  return state.buildings[tile.cityId] ?? null;
}

export { citySprawl, rivalSprawlCity, SPRAWL_TILES, type SprawlTile, visibleSprawl };
